import type { FC } from 'react';
import { useState, useCallback, useMemo } from 'react';
import { Button, Dropdown, Select, Switch, Tag, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import {
    EllipsisOutlined,
    ExclamationCircleOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    PlusCircleOutlined,
} from '@ant-design/icons';
import SearchBox from 'components/molecules/SearchBox';
import FilterIcon from 'assets/svg/Filter';
import NewGlobalFilter from 'components/molecules/common/NewGlobalFilter/NewGlobalFilter';
import FilterRibbon from 'components/molecules/PricingSimulator/FilterRibbon/FilterRibbon';
import { tpoGenUrls } from 'config/urls';
import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import type { GlobalFilterState } from 'components/molecules/common/NewGlobalFilter/hooks/useGlobalFilterState';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import { useResetRuleSession } from '../../rules/store/useResetRuleSession';
import { sendNotification } from 'utils/helperMethods';

export interface CalendarToolbarProps {
    planId: string;
    selectedColumnKeys: string[];
    setSelectedColumnKeys: (keys: string[]) => void;
    searchKeyword: string;
    setSearchKeyword: (kw: string) => void;
    groupBy: string;
    setGroupBy: (val: string) => void;
    filterState: GlobalFilterState;
    showChanges: boolean;
    onToggleShowChanges: () => void;
    onShowDeletePlan: () => void;
    onShowDeleteAllPromotions: () => void;
    onShowImportModal: () => void;
    onShowOptimizePlan: () => void;
    isOptimizationInProgress?: boolean;
    optimizationCompleted?: boolean;
    showOptimizedCalendar?: boolean;
    onToggleOptimizedView?: () => void;
    onShowOptimisationResults: () => void;
    isInfeasible?: boolean;
    infeasibleMessage?: string | null;
    isDataLoading?: boolean;
}

const CalendarToolbar: FC<CalendarToolbarProps> = ({
    planId,
    selectedColumnKeys,
    setSelectedColumnKeys,
    setSearchKeyword,
    setGroupBy,
    filterState,
    showChanges,
    onToggleShowChanges,
    onShowDeletePlan,
    onShowDeleteAllPromotions,
    onShowImportModal,
    onShowOptimizePlan,
    isOptimizationInProgress = false,
    optimizationCompleted = false,
    showOptimizedCalendar = false,
    onToggleOptimizedView,
    onShowOptimisationResults,
    isInfeasible = false,
    infeasibleMessage = null,
    isDataLoading = false,
}) => {
    const tpoGenConfig = useTpoGenConfig();
    const { allDefaultColumns, planActionMenuItems } = tpoGenConfig;
    const [search, setSearch] = useState('');
    const { setActiveView, setEditEventId } = usePlanDetailsContext();
    const { resetEventSession } = useResetRuleSession();
    const nonRemovableColumnKeys = useMemo(
        () => new Set(allDefaultColumns.filter((col) => col.required).map((col) => col.key)),
        [allDefaultColumns],
    );
    const columnLabelByKey = useMemo(
        () =>
            allDefaultColumns.reduce<Record<string, string>>((acc, col) => {
                acc[col.key] = col.label;
                return acc;
            }, {}),
        [allDefaultColumns],
    );

    const handleColumnSelectionChange = useCallback(
        (value: string[]) => {
            const removedRequiredColumnKey = selectedColumnKeys.find(
                (key) => nonRemovableColumnKeys.has(key) && !value.includes(key),
            );

            if (removedRequiredColumnKey) {
                sendNotification('error', `${columnLabelByKey[removedRequiredColumnKey]} column cannot be unselected.`);
                return;
            }

            setSearch('');
            setSearchKeyword('');
            setSelectedColumnKeys(value);
        },
        [columnLabelByKey, nonRemovableColumnKeys, selectedColumnKeys, setSearchKeyword, setSelectedColumnKeys],
    );

    const resolvedMenuItems = useMemo(
        () =>
            planActionMenuItems?.map((item) => {
                if (item && 'key' in item && item.key === 'show_changes') {
                    return {
                        ...item,
                        label: showChanges ? 'Hide Changes' : 'Show Changes',
                        icon: showChanges ? <EyeInvisibleOutlined /> : <EyeOutlined />,
                    };
                }
                if (item && 'key' in item && item.key === 'optimisation_results') {
                    return {
                        ...item,
                        disabled: !optimizationCompleted,
                    };
                }
                // Trend chart is a Metric-page-only feature; never available on Calendar.
                if (item && 'key' in item && item.key === 'view_trend_chart') {
                    return { ...item, disabled: true };
                }
                return item;
            }),
        [planActionMenuItems, showChanges, optimizationCompleted],
    );

    const handlePlanActionClick: MenuProps['onClick'] = useCallback(
        ({ key }: { key: string }) => {
            if (key === 'show_changes') {
                onToggleShowChanges();
                return;
            }
            if (key === 'delete_plan') {
                onShowDeletePlan();
                return;
            }
            if (key === 'delete_all_promotions') {
                onShowDeleteAllPromotions();
                return;
            }
            if (key === 'optimisation_results') {
                onShowOptimisationResults();
                return;
            }
        },
        [onToggleShowChanges, onShowDeletePlan, onShowDeleteAllPromotions, onShowOptimisationResults],
    );

    return (
        <>
            <div className="flex flex-row justify-between items-center gap-x-4 flex-wrap my-4">
                <div>
                    <Select
                        mode="tags"
                        style={{ width: '15rem' }}
                        value={selectedColumnKeys}
                        onChange={handleColumnSelectionChange}
                        disabled={isDataLoading}
                        options={allDefaultColumns.map((col) => ({
                            label: col.label,
                            value: col.key,
                        }))}
                        tagRender={(props: any) => <Tag>{props.label}</Tag>}
                        dropdownRender={(menu: any) => <div className="flex flex-col gap-y-2">{menu}</div>}
                    />
                </div>
                <div className="flex flex-row justify-start items-center gap-x-4">
                    <SearchBox search={search} setSearch={setSearch} onSearch={() => setSearchKeyword(search)} disabled={isDataLoading} />
                    <div className="flex flex-row justify-start items-center gap-x-2">
                        <p className="text-[#52525C] text-xs">{'Group by:'}</p>
                        <Select
                            style={{ width: '10rem' }}
                            defaultValue={'none'}
                            options={tpoGenConfig.calendarGroupByOptions}
                            onChange={(value) => setGroupBy(value)}
                            disabled={isDataLoading}
                        />
                    </div>
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    label: 'Add New Event',
                                    key: 'add_new_event',
                                    onClick: () => {
                                        // Clear any stale cross-section working set from a prior
                                        // event/promo session so a brand-new event starts clean
                                        // (the rule store outlives individual event forms).
                                        resetEventSession();
                                        setActiveView('event');
                                        setEditEventId(null);
                                    },
                                },
                                {
                                    label: 'Add Events from Plan',
                                    key: 'add_events_from_plan',
                                    onClick: () => onShowImportModal(),
                                },
                                { label: 'Add Events from Promo', key: 'add_events_from_promo', disabled: true },
                            ],
                        }}
                        disabled={isDataLoading}
                    >
                        <Button disabled={isDataLoading}>
                            <div className="flex flex-row justify-start items-center gap-x-2 font-semibold text-sm">
                                <PlusCircleOutlined />
                                <p>{'Add Events'}</p>
                            </div>
                        </Button>
                    </Dropdown>
                    <NewGlobalFilter
                        availableFilters={filterState.availableFilters}
                        allCheckedAttribute={filterState.allCheckedAttribute}
                        allSelectedValueForAllAttribute={filterState.allSelectedValueForAllAttribute}
                        applyFilterObject={filterState.applyFilterObject}
                        resetFilterObject={filterState.resetFilterObject}
                        onSubmitHandler={filterState.onFilterSaveClickHandler}
                        url={tpoGenUrls.getCalendarDataFilterAttributes(planId)}
                        selectedFilter={filterState.selectedFilter}
                        setSelectedFilter={filterState.setSelectedFilter}
                        tempApplyFilterObject={filterState.tempApplyFilterObject}
                        setTempApplyFilterObject={filterState.setTempApplyFilterObject}
                        apiVariables={filterState.filterApiVariables}
                        filterDimensionInfo={tpoGenConfig.calendarFilterDimensionInfo}
                        showAttributePanelToLeft
                        filterIcon={<FilterIcon />}
                        disabled={isDataLoading}
                    />
                    <Button
                        type="primary"
                        onClick={onShowOptimizePlan}
                        disabled={isDataLoading || isOptimizationInProgress || (optimizationCompleted && showOptimizedCalendar)}
                        loading={isOptimizationInProgress}
                    >
                        {tpoGenConfig.labels.optimizePlan.toolbarButton}
                    </Button>
                    {optimizationCompleted && (
                        <div className="flex items-center gap-1 ml-1">
                            <span className="text-xs">Original</span>
                            <Switch checked={showOptimizedCalendar} onChange={onToggleOptimizedView} disabled={isDataLoading} />
                            <span className="text-xs">Optimized</span>
                        </div>
                    )}
                    {isInfeasible && (
                        <Tooltip title={infeasibleMessage ?? tpoGenConfig.labels.optimizePlan.notifications.infeasibleTooltipFallback}>
                            <Tag icon={<ExclamationCircleOutlined />} color="warning" className="cursor-default">
                                {tpoGenConfig.labels.optimizePlan.notifications.infeasibleTagLabel}
                            </Tag>
                        </Tooltip>
                    )}
                    <Dropdown
                        menu={{
                            items: resolvedMenuItems,
                            onClick: handlePlanActionClick,
                        }}
                        trigger={['click']}
                        overlayStyle={{ width: '12rem', borderRadius: '0.5rem' }}
                        disabled={isDataLoading}
                    >
                        <Button className="!px-2 !py-1" disabled={isDataLoading}>
                            <div className="flex flex-row justify-start items-center">
                                <EllipsisOutlined />
                            </div>
                        </Button>
                    </Dropdown>
                </div>
            </div>
            <div className="mb-3 mt-2">
                <FilterRibbon
                    allCheckedAttribute={filterState.allCheckedAttribute}
                    allSelectedValueForAllAttribute={filterState.allSelectedValueForAllAttribute}
                    applyFilterObject={filterState.applyFilterObject}
                    resetFilterObject={filterState.resetFilterObject}
                    onSubmitHandler={filterState.onFilterSaveClickHandler}
                />
            </div>
        </>
    );
};

CalendarToolbar.displayName = 'CalendarToolbar';
export default CalendarToolbar;
